import { DisplayType } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Folder';

export interface FetchOptions {
  url: string;
  urlParams: any;
}

interface IPromiseQueue {
  id: string;
  inProgress: boolean;
  nextPromises: (() => Promise<any>)[];
  useLastRequestedPromise?: boolean;
}

export interface IApiRequestQueueOptions {
  id: string;
  callback: () => Promise<any>;
  useLastRequestedPromise?: boolean;
}

export function fetchWithParameters(options: FetchOptions) {
  const query = Object.entries(options.urlParams)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value as string)}`)
    .join('&');
  const fetchUrl = `${virtualPath}${options.url}?${query}`;

  return fetch(fetchUrl, {
    headers: addAdditionalParamsToHeader({
      Accept: '*/*',
      'X-Requested-With': 'XMLHttpRequest',
    }),
  });
}

export function postWithParameters(options: FetchOptions) {
  const query = Object.entries(options.urlParams)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value as string)}`)
    .join('&');
  const fetchUrl = `${virtualPath}${options.url}?${query}`;

  return fetch(fetchUrl, {
    method: 'POST',
    headers: addAdditionalParamsToHeader({
      Accept: '*/*',
      'X-Requested-With': 'XMLHttpRequest',
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    }),
  });
}

export async function fetchHtml(options: FetchOptions) {
  const response = await fetchWithParameters(options);
  return await response.text();
}

export async function fetchHtmlWithFromBody(options: FetchOptions) {
  const url = `${virtualPath}${options.url}`;
  const response = await fetch(url, {
    body: JSON.stringify(options.urlParams),
    method: 'POST',
    headers: addAdditionalParamsToHeader({
      'Content-Type': 'application/json; charset=utf-8',
    }),
  });
  return await response.text();
}

export function addAdditionalParamsToHeader(header: HeadersInit) {
  const returnedTarget = Object.assign(header, {
    'js-client-event-session-id': Decisions.GetSessionId(),
    'Force-Studio': (window as any)['forceStudio'],
  });

  return returnedTarget;
}

export function getParameterFromUrl(request: Request, param: string): string | null {
  const url = new URL(request.url);
  return url.searchParams.get(param);
}

let promiseQueue: IPromiseQueue[] = [];
/**
 * Make a queue of api requests and resolve them one after other or use the last request.
 */
export const asyncQueue = async (option: IApiRequestQueueOptions) => {
  try {
    let promise = promiseQueue.find(q => q.id === option.id);
    if (!promise) {
      promise = {
        id: option.id,
        inProgress: false,
        nextPromises: [option.callback],
        useLastRequestedPromise: !!option.useLastRequestedPromise,
      } as IPromiseQueue;
      promiseQueue.push(promise);
    }
    if (promise.inProgress) {
      promise.nextPromises = promise.useLastRequestedPromise
        ? [option.callback]
        : [...promise.nextPromises, option.callback];
    } else {
      promise.inProgress = true;
      const nextPromise = (promise.nextPromises || []).shift();
      let result;
      if (nextPromise) result = await nextPromise();
      promise.inProgress = false;
      if (promise.nextPromises && promise.nextPromises.length) {
        const id = promise.id;
        const callback = promise.nextPromises[0];
        asyncQueue({ id, callback, useLastRequestedPromise: promise.useLastRequestedPromise }); //recussion
      } else {
        promiseQueue = promiseQueue.filter(p => p.id !== option.id); //remove
      }
      return result;
    }
  } catch (err) {
    console.error(`Getting error while resolving promise in ApiRequestQueue`, err);
  }
};

export function getCookie(name: string) {
  const cookie = document.cookie;
  const nameIndex = cookie.indexOf(name);
  if (nameIndex === -1) {
    return '';
  }
  // delineated by =, so + 1
  const valueIndex = nameIndex + name.length + 1;
  // cookies themselves delineated by ";"
  let valueEnd = cookie.indexOf(';', valueIndex);
  // cookies doesn't contains ";" in the end
  const value = valueEnd === -1 ? cookie.slice(valueIndex) : cookie.slice(valueIndex, valueEnd);
  return value || '';
}

export function getCookieDisplayType() {
  return parseInt(getCookie('ContextDisplayType'), 10);
}

export function isDisplayTypeTablet() {
  return getCookieDisplayType() === DisplayType.HtmlTablet;
}
export function isDisplayTypeMobile() {
  return getCookieDisplayType() === DisplayType.HtmlMobile;
}
